// Trainer-facing "who is this client" block — used everywhere a
// client appears in a list, picker, autocomplete, or table row.
// Renders:
//
//   ┌─────┐  Sarah Connor                  [Online]
//   │  S  │  sarah@example.com
//   └─────┘
//
// Intentionally NOT a row container — the caller wraps this in a
// <button>, <tr><td>, <label>, antd <Option>, etc. depending on
// the surface's selection model. ClientIdentity owns ONLY the
// identity block (avatar + name + email + optional tag), so the
// API stays small and surface-specific chrome (checkboxes,
// trailing pills, quick-action buttons) layers around it without
// fighting the component.
//
// API choices:
//   • Explicit `name` / `email` / `avatar` props instead of a
//     polymorphic `client` object — calls vary (`{user: {...}}`
//     vs flattened `{name, email, ...}`) and passing direct props
//     keeps the contract obvious at the call site.
//   • `gradient` overrideable so ChatDashboard can keep its
//     status-driven avatar color (green when there's a chat,
//     primary when not).
//   • `tag` is a plain string — caller formats. `formatCoachingPref`
//     ships from this file so callers reaching for the common
//     pref-label pattern don't duplicate the enum mapping.

import React from 'react';
import Avatar from '../Avatar';
import './index.css';

export default function ClientIdentity({
  name,
  email,
  avatar,
  gradient,
  size = 32,
  tag,
  className = '',
}) {
  // Fallback when both name + email are empty (new invite that
  // hasn't filled anything in yet) — avoid rendering a bare empty
  // row that the trainer can't read.
  const displayName = (name || '').trim() || email || 'Client';
  return (
    <div className={`ci-row ${className}`.trim()}>
      <Avatar
        size={size}
        src={avatar}
        name={displayName}
        gradient={gradient}
      />
      <div className="ci-text">
        <div className="ci-name">{displayName}</div>
        {email ? <div className="ci-email">{email}</div> : null}
      </div>
      {tag ? <span className="ci-tag">{tag}</span> : null}
    </div>
  );
}

// Pretty-print the coaching pref enum for display.
//   online_coaching  → "Remote"
//   onsite_coaching  → "In-person"
//   hybrid_coaching  → "Hybrid"
//   fixed_program    → "Fixed"
// Unknown / missing values return '' so the caller can skip
// rendering the tag entirely.
export function formatCoachingPref(raw) {
  if (!raw || typeof raw !== 'string') return '';
  const map = {
    online_coaching: 'Remote',
    onsite_coaching: 'In-person',
    hybrid_coaching: 'Hybrid',
    fixed_program: 'Fixed',
  };
  if (map[raw]) return map[raw];
  // Generic fallback for any future enum value we haven't mapped
  // yet — snake_case → "Title Case" without suffix stripping.
  return raw
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
}
